import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Droplets, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaterTracker = ({ token }) => {
  const { t } = useTranslation();
  const [waterData, setWaterData] = useState({ total_ml: 0, goal_ml: 2000, logs: [] });
  const [amount, setAmount] = useState(250);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchWaterData();
  }, []);

  const fetchWaterData = async () => {
    try {
      const response = await axios.get(`${API}/water/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaterData(response.data);
    } catch (error) {
      console.error('Error fetching water data:', error);
    }
  };

  const logWater = async () => {
    if (amount <= 0) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/water/log?amount_ml=${amount}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchWaterData();
      setAmount(250);
    } catch (error) {
      console.error('Error logging water:', error);
    } finally {
      setLoading(false);
    }
  };

  const percentage = (waterData.total_ml / waterData.goal_ml) * 100;

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
      <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
        <Droplets className="text-[#FF6600]" size={20} />
        {t('water.tracker')}
      </h2>
      
      <div className="flex items-center gap-4 mb-4">
        <div style={{ width: 80, height: 80 }}>
          <CircularProgressbar
            value={percentage}
            text={`${Math.round(percentage)}%`}
            styles={buildStyles({
              textColor: '#FF6600',
              pathColor: '#FF6600',
              trailColor: '#f3f4f6',
              textSize: '20px'
            })}
          />
        </div>
        
        <div className="flex-1">
          <div className="text-xl font-bold">{waterData.total_ml} ml</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">of {waterData.goal_ml} ml goal</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {waterData.goal_ml - waterData.total_ml > 0 
              ? `${waterData.goal_ml - waterData.total_ml} ml remaining`
              : 'Goal reached! 🎉'}
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          className="flex-1 p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF6600]"
          placeholder="Amount (ml)"
        />
        <button
          onClick={logWater}
          disabled={loading}
          className="px-4 py-2 bg-[#FF6600] text-white rounded-lg font-semibold text-sm flex items-center gap-1 hover:bg-[#E55C00]"
        >
          <Plus size={16} />
          Add
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {[250, 500, 750, 1000].map((preset) => (
          <button
            key={preset}
            onClick={() => setAmount(preset)}
            className="py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200"
          >
            {preset}ml
          </button>
        ))}
      </div>
    </div>
  );
};

export default WaterTracker;
